import { useCallback, useRef } from "react";
import { type FixedSizeListProps } from "react-window";

export interface Props {
  loadMore: Function;
  threshold?: number; // px from list end at which to trigger
  horizontal?: boolean;
  hasMore?: boolean; // externally provided flag to stop further loads
}

type OnScrollParams = Parameters<NonNullable<FixedSizeListProps["onScroll"]>>[0];

export function useEndReached({ loadMore, threshold = 200, horizontal = false, hasMore = true }: Props) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const lastTriggeredSizeRef = useRef<number | null>(null);

  const onScroll = useCallback(
    ({ scrollOffset, scrollDirection, scrollUpdateWasRequested }: OnScrollParams) => {
      // Ignore programmatic scrolls
      if (scrollUpdateWasRequested) return;

      const el = outerRef.current;
      if (!el) return;

      const viewport = horizontal ? el.clientWidth : el.clientHeight;
      const scrollSize = horizontal ? el.scrollWidth : el.scrollHeight;

      const distanceFromEnd = scrollSize - (scrollOffset + viewport);

      if (
        scrollDirection === "forward" &&
        distanceFromEnd <= threshold &&
        hasMore &&
        !loadingRef.current &&
        lastTriggeredSizeRef.current !== scrollSize
      ) {
        lastTriggeredSizeRef.current = scrollSize;
        loadingRef.current = true;
        Promise.resolve(loadMore()).finally(() => {
          loadingRef.current = false;
        });
      }
    },
    [horizontal, threshold, loadMore, hasMore],
  );

  // When items grow, allow triggering again
  const resetEndGuard = useCallback(() => {
    lastTriggeredSizeRef.current = null;
  }, []);

  return { outerRef, onScroll, resetEndGuard };
}
